import { findIndex } from 'lodash';
import {
  getFestivalAction,
  getFestivalProductionsAction,
  getFestivalContactsAction,
  updateFestivalAction,
  getCompaniesAction,
  getFestivalDescriptionsAction,
  createFestivalDescriptionAction,
  deleteFestivalDescriptionAction,
  updateFestivalDescriptionAction,
  addAndUpdateFestivalContactsAction,
  updateFestivalInfoAction,
  getFestivalVenuesAction,
  getFestivalVideosAction,
  addFestivalVideoAction,
  updateFestivalVideoAction,
  getFestivalLSVodAction,
  getFestivalImagesAction,
  getFestivalEmployeesAction,
  getFestivalBannerAction,
  getFestivalProductionImagesAction,
  getFestivalProductionVideosAction,
  getFestivalSearchProductionsAction,
  getFestivalTranslationsAction,
  deleteFestivalTranslationsAction,
  getFestivalYoungArtistProgramAction,
  createFestivalYoungArtistProgramAction,
  deleteFestivalYoungArtistProgramAction,
  getFestivalCompaniesAction,
  createFestivalCompanyAction,
  deleteFestivalCompanyAction,
  getAboutFestivalVideoAction,
  getFestivalReviewsAction,
  getFestivalProductionReviewsAction,
} from './actions';
import { namedEntityParams, entityReducer, namedListParams, listReducer } from '../../utils/crudUtils';
import { CLEAR_FESTIVAL_PRODUCTIONS, DELETE_FESTIVAL_VIDEO, SET_FESTIVAL, SET_INIT_PRODUCTIONS } from './constants';

export const key = 'Festival';

export const initialState = {
  contacts: namedListParams('contacts'),
  festivalContactsUpdating: false, // as there are several requests and festival info update
  festival: namedEntityParams('festival'),
  images: namedListParams('images'),
  productionImages: namedListParams('productionImages'),
  productionVideos: namedListParams('productionVideos'),
  performances: namedListParams('performances'),
  companies: namedListParams('companies'),
  searchProductions: namedListParams('searchProductions'),
  loadingFieldName: '',
  descriptions: namedListParams('descriptions'),
  venues: namedListParams('venues'),
  viewInitialProductionsData: {
    data: {},
    query: {},
  },
  videos: namedListParams('videos'),
  lsVod: namedListParams('lsVod'),
  employees: namedListParams('employees'),
  banner: namedListParams('banner'),
  aboutFestivalVideo: namedListParams('aboutFestivalVideo'),
  reviews: namedListParams('reviews'),
  productionReviews: namedListParams('productionReviews'),
};

const festivalReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case getFestivalAction.TRIGGER:
    case getFestivalAction.SUCCESS:
    case getFestivalAction.FAILURE:
      return entityReducer(state, type, payload, initialState.festival.entityName);

    case getFestivalContactsAction.TRIGGER:
    case getFestivalContactsAction.SUCCESS:
    case getFestivalContactsAction.FAILURE:
      return listReducer(state, type, payload, initialState.contacts.listName);

    case getFestivalImagesAction.TRIGGER:
    case getFestivalImagesAction.SUCCESS:
    case getFestivalImagesAction.FAILURE:
      return listReducer(state, type, payload, initialState.images.listName);

    case getFestivalProductionImagesAction.TRIGGER:
    case getFestivalProductionImagesAction.SUCCESS:
    case getFestivalProductionImagesAction.FAILURE:
      return listReducer(state, type, payload, initialState.productionImages.listName);

    case getFestivalProductionVideosAction.TRIGGER:
    case getFestivalProductionVideosAction.SUCCESS:
    case getFestivalProductionVideosAction.FAILURE:
      return listReducer(state, type, payload, initialState.productionVideos.listName);

    case getFestivalBannerAction.TRIGGER:
    case getFestivalBannerAction.SUCCESS:
    case getFestivalBannerAction.FAILURE:
      return listReducer(state, type, payload, initialState.banner.listName);

    case getFestivalProductionsAction.TRIGGER:
    case getFestivalProductionsAction.SUCCESS:
    case getFestivalProductionsAction.FAILURE:
      return listReducer(state, type, payload, initialState.performances.listName);

    case updateFestivalInfoAction.TRIGGER: // as we update company info with contacts together
    case addAndUpdateFestivalContactsAction.TRIGGER:
      return { ...state, festivalContactsUpdating: true };
    case addAndUpdateFestivalContactsAction.SUCCESS:
    case addAndUpdateFestivalContactsAction.FAILURE:
      return { ...state, festivalContactsUpdating: false };

    case SET_FESTIVAL:
      return {
        ...state,
        [initialState.festival.entityName]: {
          ...state[initialState.festival.entityName],
          entity: payload,
          loadedOnce: true,
        },
      };

    // for display pages only
    case SET_INIT_PRODUCTIONS:
      return {
        ...state,
        viewInitialProductionsData: {
          data: payload?.data,
          query: payload?.query,
        },
      };

    case updateFestivalAction.TRIGGER:
    case updateFestivalAction.SUCCESS:
    case updateFestivalAction.FAILURE:
      return {
        ...listReducer(state, type, payload, initialState.festival.entityName),
        loadingFieldName: payload?.fieldName || '',
      };

    case getCompaniesAction.TRIGGER:
    case getCompaniesAction.SUCCESS:
    case getCompaniesAction.FAILURE:
      return {
        ...listReducer(state, type, payload, initialState.companies.listName),
        loadingFieldName: payload?.fieldName || '',
      };

    case getFestivalDescriptionsAction.TRIGGER:
    case getFestivalDescriptionsAction.SUCCESS:
    case getFestivalDescriptionsAction.FAILURE:
      return listReducer(state, type, payload, initialState.descriptions.listName);

    case getFestivalSearchProductionsAction.TRIGGER:
    case getFestivalSearchProductionsAction.SUCCESS:
    case getFestivalSearchProductionsAction.FAILURE:
      return listReducer(state, type, payload, initialState.searchProductions.listName);

    case createFestivalDescriptionAction.TRIGGER:
    case updateFestivalDescriptionAction.TRIGGER:
      return { ...state, descriptions: { ...state.descriptions, isDescriptionUpdating: true } };
    case createFestivalDescriptionAction.FAILURE:
    case updateFestivalDescriptionAction.FAILURE:
      return { ...state, descriptions: { ...state.descriptions, isDescriptionUpdating: true } };

    case createFestivalDescriptionAction.SUCCESS:
      return {
        ...state,
        descriptions: {
          ...state.descriptions,
          data: [...state.descriptions.data, payload.entity],
          isDescriptionUpdating: false,
        },
      };

    case deleteFestivalDescriptionAction.TRIGGER:
      return { ...state, descriptions: { ...state.descriptions, loading: true } };
    case deleteFestivalDescriptionAction.SUCCESS: {
      return {
        ...state,
        descriptions: {
          ...state.descriptions,
          data: state.descriptions.data.filter(d => d?.id !== payload?.id),
          loading: false,
        },
      };
    }
    case deleteFestivalDescriptionAction.FAILURE:
      return { ...state, descriptions: { ...state.descriptions, loading: false } };

    case updateFestivalDescriptionAction.SUCCESS: {
      const updatedDescriptionIndex = findIndex(state?.descriptions?.data, { id: payload?.entity?.id });
      return {
        ...state,
        descriptions: {
          ...state.descriptions,
          data: Object.assign([], state.descriptions.data, { [updatedDescriptionIndex]: payload.entity }),
          isDescriptionUpdating: false,
        },
      };
    }

    case CLEAR_FESTIVAL_PRODUCTIONS:
      return { ...state, [initialState.performances.listName]: namedListParams(initialState.performances.listName) };

    case getFestivalVenuesAction.TRIGGER:
    case getFestivalVenuesAction.SUCCESS:
    case getFestivalVenuesAction.FAILURE:
      return listReducer(state, type, payload, initialState.venues.listName);

    case getFestivalVideosAction.FAILURE:
    case getFestivalVideosAction.TRIGGER:
    case getFestivalVideosAction.SUCCESS:
      return listReducer(state, type, payload, initialState.videos.listName);

    case getFestivalLSVodAction.FAILURE:
    case getFestivalLSVodAction.TRIGGER:
    case getFestivalLSVodAction.SUCCESS:
      return listReducer(state, type, payload, initialState.lsVod.listName);

    case addFestivalVideoAction.TRIGGER:
      return { ...state, mediaUpdating: true };
    case addFestivalVideoAction.SUCCESS: {
      return {
        ...state,
        videos: { ...state.videos, data: [...state.videos.data, payload?.entity] },
        mediaUpdating: false,
      };
    }
    case addFestivalVideoAction.FAILURE:
      return { ...state, mediaUpdating: false };

    case updateFestivalVideoAction.TRIGGER:
      return { ...state, mediaUpdating: true };
    case updateFestivalVideoAction.SUCCESS: {
      const videos = [...state?.videos?.data];
      const updatedReviewIndex = findIndex(state?.videos?.data, { id: payload?.entity?.id });
      videos.splice(updatedReviewIndex, 1, payload?.entity);
      return { ...state, videos: { ...state.videos, data: videos }, mediaUpdating: false };
    }
    case updateFestivalVideoAction.FAILURE:
      return { ...state, mediaUpdating: false };

    case DELETE_FESTIVAL_VIDEO:
      return { ...state, videos: { ...state.videos, data: state.videos.data.filter(i => i?.id !== payload?.id) } };

    case getFestivalEmployeesAction.TRIGGER:
    case getFestivalEmployeesAction.SUCCESS:
    case getFestivalEmployeesAction.FAILURE:
      return listReducer(state, type, payload, initialState.employees.listName);
    case getFestivalTranslationsAction.TRIGGER:
    case getFestivalTranslationsAction.SUCCESS:
    case getFestivalTranslationsAction.FAILURE:
      return listReducer(state, type, payload, 'translations');
    case deleteFestivalTranslationsAction.TRIGGER:
    case deleteFestivalTranslationsAction.FAILURE:
      return { ...state, festivals: { ...state.translations, loading: true } };
    case deleteFestivalTranslationsAction.SUCCESS: {
      return {
        ...state,
        translations: {
          ...state.translations,
          data: state.translations.data.filter(d => d?.id !== payload?.id),
          loading: false,
        },
      };
    }
    case getFestivalYoungArtistProgramAction.TRIGGER:
    case getFestivalYoungArtistProgramAction.SUCCESS:
    case getFestivalYoungArtistProgramAction.FAILURE:
      return listReducer(state, type, payload, 'youngArtistProgram');
    case deleteFestivalYoungArtistProgramAction.TRIGGER:
    case deleteFestivalYoungArtistProgramAction.FAILURE:
      return { ...state, youngArtistProgram: { ...state.youngArtistProgram, loading: true } };
    case deleteFestivalYoungArtistProgramAction.SUCCESS: {
      return {
        ...state,
        youngArtistProgram: {
          ...state.youngArtistProgram,
          data: state.youngArtistProgram.data.filter(d => d?.id !== payload?.id),
          loading: false,
        },
      };
    }
    case createFestivalYoungArtistProgramAction.SUCCESS:
      return {
        ...state,
        youngArtistProgram: { ...state.youngArtistProgram, data: [...state.youngArtistProgram.data, payload.entity] },
      };
    case getFestivalCompaniesAction.TRIGGER:
    case getFestivalCompaniesAction.SUCCESS:
    case getFestivalCompaniesAction.FAILURE:
      return listReducer(state, type, payload, 'companies');
    case deleteFestivalCompanyAction.TRIGGER:
    case deleteFestivalCompanyAction.FAILURE:
      return { ...state, companies: { ...state.companies, loading: true } };
    case deleteFestivalCompanyAction.SUCCESS: {
      return {
        ...state,
        companies: {
          ...state.companies,
          data: state.companies.data.filter(d => d?.id !== payload?.id),
          loading: false,
        },
      };
    }
    case createFestivalCompanyAction.SUCCESS:
      return {
        ...state,
        companies: { ...state.companies, data: [...state.companies.data, payload.entity] },
      };
    case getAboutFestivalVideoAction.TRIGGER:
    case getAboutFestivalVideoAction.SUCCESS:
    case getAboutFestivalVideoAction.FAILURE:
      return listReducer(state, type, payload, 'aboutFestivalVideo');

    case getFestivalReviewsAction.TRIGGER:
    case getFestivalReviewsAction.SUCCESS:
    case getFestivalReviewsAction.FAILURE:
      return listReducer(state, type, payload, state?.reviews?.listName);

    case getFestivalProductionReviewsAction.TRIGGER:
    case getFestivalProductionReviewsAction.SUCCESS:
    case getFestivalProductionReviewsAction.FAILURE:
      return listReducer(state, type, payload, state?.productionReviews?.listName);
    default:
      return state;
  }
};

export default festivalReducer;
